import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/apiClient';
import PageHeader from '../components/PageHeader';
import Modal from '../components/Modal';
import useAuth from '../hooks/useAuth';
import SortFilter, { sortItems } from '../components/SortFilter';

export default function ActivitiesPage() {
  const navigate = useNavigate();
  const { user, currentBranch } = useAuth();
  const [activities, setActivities] = useState([]);
  const [branches, setBranches] = useState([]);
  const [sortOption, setSortOption] = useState('newest');
  const [filters, setFilters] = useState({ 
    branch_id: currentBranch?.branch_id || '', 
    date: '' 
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [previewPhoto, setPreviewPhoto] = useState(null);

  // Edit State
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', category: '', description: '', activity_date: '', activity_time: '' });
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [removedPhotos, setRemovedPhotos] = useState([]);
  const [editPhotos, setEditPhotos] = useState([]);

  useEffect(() => {
    loadBranches();
  }, []);

  useEffect(() => {
    if (currentBranch) {
      setFilters(p => ({ ...p, branch_id: currentBranch.branch_id }));
    }
  }, [currentBranch]);

  useEffect(() => {
    load();
  }, [filters.branch_id, filters.date]);

  async function loadBranches() {
    try {
      const res = await api.get('/admin/branches');
      setBranches(res.data.data || []);
    } catch (err) {}
  }

  async function load() {
    try {
      const params = { limit: 50 };
      if (filters.branch_id) params.branch_id = filters.branch_id;
      if (filters.date) params.date = filters.date;
      const response = await api.get('/activities', { params });
      const data = response.data.data || [];
      setActivities(data);
      if (selectedActivity) {
        const updatedSelected = data.find(a => a.id === selectedActivity.id);
        if (updatedSelected) setSelectedActivity(updatedSelected);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load activities');
    }
  }

  const updateStatus = async (id, newStatus) => {
    setError('');
    setSuccessMessage('');
    try {
      await api.put(`/activities/${id}/status`, { status: newStatus });
      setSuccessMessage(`Activity marked as ${newStatus}`);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update status');
    }
  };

  const removeActivity = async (id) => {
    if (!window.confirm('Are you sure you want to delete this activity?')) return;
    setError('');
    setSuccessMessage('');
    try {
      await api.delete(`/activities/${id}`);
      setSuccessMessage('Activity deleted');
      if (selectedActivity?.id === id) setSelectedActivity(null);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to delete activity');
    }
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatTimeForInput = (timeStr) => {
    if (!timeStr) return '';
    if (/^\d{2}:\d{2}$/.test(timeStr)) return timeStr;
    if (/^\d{2}:\d{2}:\d{2}$/.test(timeStr)) return timeStr.substring(0, 5);
    const match = timeStr.trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)$/i);
    if (match) {
      let hours = parseInt(match[1], 10);
      const minutes = match[2];
      const ampm = match[3].toUpperCase();
      if (ampm === 'PM' && hours < 12) hours += 12;
      if (ampm === 'AM' && hours === 12) hours = 0;
      return `${String(hours).padStart(2, '0')}:${minutes}`;
    }
    return timeStr;
  };

  const formatTimeForSave = (time24) => {
    if (!time24) return '';
    if (!/^\d{2}:\d{2}$/.test(time24)) return time24;
    const [h, m] = time24.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  const startEdit = (activity) => {
    setEditForm({
      title: activity.title || '',
      category: activity.category || '',
      description: activity.description || '',
      activity_date: formatDateForInput(activity.activity_date),
      activity_time: formatTimeForInput(activity.activity_time)
    });
    setExistingPhotos(activity.photos || []);
    setRemovedPhotos([]);
    setEditPhotos([]);
    setIsEditing(true);
  };

  const removeExistingPhoto = (photoUrl) => {
    setExistingPhotos(prev => prev.filter(p => p !== photoUrl));
    setRemovedPhotos(prev => [...prev, photoUrl]);
  };

  const saveActivityEdit = async (e) => {
    if (e) e.preventDefault();
    setError('');
    setSuccessMessage('');
    try {
      const formData = new FormData();
      formData.append('title', editForm.title);
      formData.append('category', editForm.category);
      formData.append('description', editForm.description);
      formData.append('activity_date', editForm.activity_date);
      formData.append('activity_time', formatTimeForSave(editForm.activity_time));
      if (removedPhotos.length > 0) {
        formData.append('remove_photos', JSON.stringify(removedPhotos));
      }
      if (editPhotos && editPhotos.length > 0) {
        editPhotos.forEach(file => formData.append('photos', file));
      }
      await api.put(`/activities/${selectedActivity.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSuccessMessage('Activity updated successfully.');
      setIsEditing(false);
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to update activity');
    }
  };

  const sortedActivities = sortItems(activities, sortOption, 'title', 'activity_date');

  return (
    <div>
      <PageHeader title="Daily Activity Management" subtitle="Monitor teacher uploads, photos, comments, and daily reports" />
      {successMessage && <div className="success-message">{successMessage}</div>}
      {error && <div className="error-message">{error}</div>}
      
      <div style={{ display: 'flex', gap: 12, padding: '0 0 16px', alignItems: 'center', flexWrap: 'wrap' }}>
        {user?.role === 'admin' ? (
          <select
            value={filters.branch_id}
            onChange={(e) => setFilters(p => ({ ...p, branch_id: e.target.value }))}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, minWidth: 180, cursor: 'pointer', background: '#ffffff' }}
          >
            <option value="">All branches</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        ) : (
          <div style={{
            padding: '8px 16px',
            background: '#f3f4f6',
            border: '1px solid #cbd5e1',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: 500,
            color: '#374151'
          }}>
            Branch: {currentBranch?.name || 'No Branch Selected'}
          </div>
        )}

        <input
          type="date"
          value={filters.date}
          onChange={(e) => setFilters(p => ({ ...p, date: e.target.value }))}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, cursor: 'pointer', background: '#ffffff' }}
        />

        {((user?.role === 'admin' && filters.branch_id) || filters.date) && (
          <button
            onClick={() => setFilters({ branch_id: user?.role === 'center_manager' ? currentBranch?.branch_id || '' : '', date: '' })}
            style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: 13 }}
          >
            Clear
          </button>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
          <SortFilter sortOption={sortOption} onSortChange={setSortOption} />
        </div>
      </div>
      
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Student Name</th>
              <th>Teacher</th>
              <th>Title</th>
              <th>Category</th>
              <th>Date</th>
              <th>Photos</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {sortedActivities.map((activity) => (
              <tr key={activity.id}>
                <td>{activity.id}</td>
                <td style={{ whiteSpace: 'normal', minWidth: '240px', maxWidth: '380px' }}>
                  {activity.students && activity.students.length > 0 ? (
                    <div style={{ display: 'flex', gap: '4px 6px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {activity.students.map(s => (
                        <span
                          key={s.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/students/${s.id}?from=activities`);
                          }}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            color: '#1d4ed8',
                            cursor: 'pointer',
                            fontWeight: 600,
                            background: '#eff6ff',
                            padding: '3px 8px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            border: '1px solid #bfdbfe',
                            whiteSpace: 'nowrap',
                            transition: 'all 0.15s ease'
                          }}
                        >
                          👤 {s.full_name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: '#94a3b8' }}>-</span>
                  )}
                </td>
                <td
                  onClick={() => activity.teacher_id && navigate(`/teachers/${activity.teacher_id}?from=activities`)}
                  style={{
                    cursor: activity.teacher_id ? 'pointer' : 'default',
                    color: activity.teacher_id ? '#2563eb' : 'inherit',
                    fontWeight: activity.teacher_id ? 500 : 400
                  }}
                >
                  {activity.teacher_name || '-'}
                </td>
                <td>{activity.title || '-'}</td>
                <td>{activity.category || '-'}</td>
                <td>{activity.activity_date || '-'}</td>
                <td>{activity.photos?.length || 0}</td>
                <td>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontSize: '12px',
                    fontWeight: 600,
                    textTransform: 'capitalize',
                    background: activity.status === 'approved' ? '#d1fae5' : activity.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                    color: activity.status === 'approved' ? '#065f46' : activity.status === 'rejected' ? '#991b1b' : '#92400e'
                  }}>
                    {activity.status || 'pending'}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <button
                      type="button"
                      className="text-button"
                      style={{ color: '#2563eb', fontWeight: 600 }}
                      onClick={() => {
                        setSelectedActivity(activity);
                        setIsEditing(false);
                      }}
                    >
                      Detail
                    </button>

                    {activity.status !== 'approved' && (
                      <button
                        type="button"
                        className="text-button"
                        style={{ color: '#10b981', fontWeight: 600 }}
                        onClick={() => updateStatus(activity.id, 'approved')}
                      >
                        Approve
                      </button>
                    )}
                    {activity.status !== 'rejected' && (
                      <button
                        type="button"
                        className="text-button"
                        style={{ color: '#ef4444', fontWeight: 600 }}
                        onClick={() => updateStatus(activity.id, 'rejected')}
                      >
                        Reject
                      </button>
                    )}
                    <button type="button" className="text-button danger" onClick={() => removeActivity(activity.id)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Activity Detail / Edit Modal */}
      {selectedActivity && (
        <Modal
          isOpen={!!selectedActivity}
          onClose={() => {
            setSelectedActivity(null);
            setIsEditing(false);
          }}
          title={isEditing ? `Edit Activity #${selectedActivity.id}` : `Activity Details #${selectedActivity.id}`}
        >
          {isEditing ? (
            <form onSubmit={saveActivityEdit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Title</label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm(p => ({ ...p, title: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                  required
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Category</label>
                  <input
                    type="text"
                    value={editForm.category}
                    onChange={(e) => setEditForm(p => ({ ...p, category: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Activity Date</label>
                  <input
                    type="date"
                    value={editForm.activity_date}
                    onChange={(e) => setEditForm(p => ({ ...p, activity_date: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14 }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Activity Time</label>
                  <input
                    type="time"
                    value={editForm.activity_time}
                    onChange={(e) => setEditForm(p => ({ ...p, activity_time: e.target.value }))}
                    style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, cursor: 'pointer' }}
                    required
                  />
                </div>
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>Description</label>
                <textarea
                  rows={4}
                  value={editForm.description}
                  onChange={(e) => setEditForm(p => ({ ...p, description: e.target.value }))}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 14, lineHeight: 1.5 }}
                />
              </div>

              {/* Current Photos list with remove option */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                  Current Photos ({existingPhotos.length})
                </label>
                {existingPhotos.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 10, marginBottom: 12 }}>
                    {existingPhotos.map((photoUrl, index) => (
                      <div key={index} style={{ position: 'relative', aspectRatio: '1', borderRadius: 8, overflow: 'hidden', border: '1px solid #cbd5e1' }}>
                        <img src={photoUrl} alt="Photo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <button
                          type="button"
                          title="Remove Photo"
                          onClick={() => removeExistingPhoto(photoUrl)}
                          style={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            background: '#ef4444',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '50%',
                            width: 22,
                            height: 22,
                            fontSize: 12,
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 10 }}>No existing photos.</div>
                )}
              </div>

              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                  Upload Additional Photos (Optional)
                </label>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={(e) => setEditPhotos(Array.from(e.target.files))}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13 }}
                />
              </div>

              <div className="modal-actions" style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16, marginTop: 8 }}>
                <button type="button" className="secondary-button" onClick={() => setIsEditing(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary-button">
                  Save Changes
                </button>
              </div>
            </form>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Top Summary Bar */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px',
                background: '#f8fafc',
                borderRadius: '10px',
                border: '1px solid #e2e8f0'
              }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                    {selectedActivity.title || selectedActivity.category}
                  </h3>
                  <span style={{ fontSize: 13, color: '#64748b' }}>
                    Category: <strong>{selectedActivity.category || '-'}</strong>
                  </span>
                </div>
                <span style={{
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '13px',
                  fontWeight: 600,
                  textTransform: 'capitalize',
                  background: selectedActivity.status === 'approved' ? '#d1fae5' : selectedActivity.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                  color: selectedActivity.status === 'approved' ? '#065f46' : selectedActivity.status === 'rejected' ? '#991b1b' : '#92400e'
                }}>
                  {selectedActivity.status || 'pending'}
                </span>
              </div>

              {/* Info Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: 12,
                padding: '12px 16px',
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '10px'
              }}>
                <div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Teacher</div>
                  <div style={{ fontSize: 14, color: '#1e293b', fontWeight: 600 }}>{selectedActivity.teacher_name || '-'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Branch</div>
                  <div style={{ fontSize: 14, color: '#1e293b', fontWeight: 600 }}>{selectedActivity.branch_name || '-'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Date</div>
                  <div style={{ fontSize: 14, color: '#1e293b', fontWeight: 600 }}>{selectedActivity.activity_date || '-'}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Time</div>
                  <div style={{ fontSize: 14, color: '#1e293b', fontWeight: 600 }}>{selectedActivity.activity_time || '-'}</div>
                </div>
              </div>

              {/* Description Box */}
              <div>
                <h4 style={{ margin: '0 0 6px', fontSize: 14, color: '#334155' }}>Description</h4>
                <div style={{
                  padding: '12px 16px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: 14,
                  color: '#334155',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.5
                }}>
                  {selectedActivity.description || <span style={{ color: '#94a3b8' }}>No description provided.</span>}
                </div>
              </div>

              {/* Tagged Students */}
              {selectedActivity.students && selectedActivity.students.length > 0 && (
                <div>
                  <h4 style={{ margin: '0 0 6px', fontSize: 14, color: '#334155' }}>Tagged Students</h4>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {selectedActivity.students.map(s => (
                      <span key={s.id} style={{
                        padding: '4px 10px',
                        background: '#eff6ff',
                        color: '#1d4ed8',
                        borderRadius: '16px',
                        fontSize: 13,
                        fontWeight: 500,
                        border: '1px solid #bfdbfe'
                      }}>
                        👤 {s.full_name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Photos Section */}
              <div>
                <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#334155' }}>
                  Photos ({selectedActivity.photos?.length || 0})
                </h4>
                {selectedActivity.photos && selectedActivity.photos.length > 0 ? (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                    gap: 10
                  }}>
                    {selectedActivity.photos.map((photoUrl, index) => (
                      <div
                        key={index}
                        onClick={() => setPreviewPhoto(photoUrl)}
                        style={{
                          position: 'relative',
                          aspectRatio: '1',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          border: '1px solid #e2e8f0',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}
                      >
                        <img
                          src={photoUrl}
                          alt={`Activity photo ${index + 1}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: '16px',
                    background: '#f8fafc',
                    border: '1px dashed #cbd5e1',
                    borderRadius: '8px',
                    textAlign: 'center',
                    color: '#94a3b8',
                    fontSize: 13
                  }}>
                    🖼️ No photos attached to this activity.
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="modal-actions" style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16, marginTop: 8 }}>
                <button
                  type="button"
                  className="primary-button"
                  style={{ background: '#2563eb', borderColor: '#2563eb' }}
                  onClick={() => startEdit(selectedActivity)}
                >
                  ✏️ Edit Activity
                </button>

                {selectedActivity.status !== 'approved' && (
                  <button
                    type="button"
                    className="primary-button"
                    style={{ background: '#10b981', borderColor: '#10b981' }}
                    onClick={() => updateStatus(selectedActivity.id, 'approved')}
                  >
                    Approve
                  </button>
                )}
                {selectedActivity.status !== 'rejected' && (
                  <button
                    type="button"
                    className="primary-button"
                    style={{ background: '#ef4444', borderColor: '#ef4444' }}
                    onClick={() => updateStatus(selectedActivity.id, 'rejected')}
                  >
                    Reject
                  </button>
                )}
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setSelectedActivity(null)}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </Modal>
      )}

      {/* Full Photo Lightbox Overlay */}
      {previewPhoto && (
        <div
          onClick={() => setPreviewPhoto(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24
          }}
        >
          <img
            src={previewPhoto}
            alt="Preview"
            style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: '8px', objectFit: 'contain' }}
          />
          <button
            onClick={() => setPreviewPhoto(null)}
            style={{
              position: 'absolute',
              top: 20,
              right: 20,
              background: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: 36,
              height: 36,
              fontSize: 20,
              cursor: 'pointer',
              fontWeight: 700
            }}
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
}

